import { FC, useMemo, useCallback } from 'react'
import styles from 'components/features/future/FutureMemberLayersPane/FutureMemberLayersPane.styl'
import { FutureLayerPreview } from 'components/features/future/FutureLayerPreview/FutureLayerPreview'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { layersUIDsSelector } from 'components/features/future/selectors'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { FloatingPaneHeader } from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader'
import { FloatingPaneFooter } from 'components/features/pfm/FloatingPaneFooter/FloatingPaneFooter'
import { modifyFloatingPaneActionCreator, openSubscriptionPageActionCreator } from 'actions/floatingPanesActions'
import { LoadingOverlay } from 'components/atoms/preloaders/LoadingOverlay'
import { isMemberPremiumSelector } from 'selectors/globalSelectors'
import { FuturePaneOverlay } from 'components/features/future/FuturePaneOverlay/FuturePaneOverlay'

type FutureMemberLayersPaneProps = {
  onCancel: () => void
}

export const FutureMemberLayersPane: FC<FutureMemberLayersPaneProps> = (props) => {
  const { onCancel } = props

  const layersUIDs = useStoreSelector(layersUIDsSelector)
  const isLoading = useStoreSelector((state) => state.data.future.request.isLoading)
  const isMemberPremium = useStoreSelector(isMemberPremiumSelector)
  const addLayerIsLocked = !isMemberPremium && layersUIDs.length >= 2

  const layersChild = useMemo(() => layersUIDs.map((uid) => (
    <li key={ uid } className={ styles.item }>
      <FutureLayerPreview uid={ uid } />
    </li>
  )), [layersUIDs])

  const headerChild = useMemo(() => (
    <FloatingPaneHeader
      title='Goals and Events'
      iconSlug='goals_and_events'
      centered
    />
  ), [])

  const dispatch = useStoreDispatch()

  const handleAddLayer = useCallback(() => {
    if (addLayerIsLocked) {
      dispatch(openSubscriptionPageActionCreator())
      return
    }

    dispatch(modifyFloatingPaneActionCreator('future', {
      mode: 'all-layers',
    }))
  }, [dispatch, addLayerIsLocked])

  const footerChild = useMemo(() => (
    <FloatingPaneFooter
      actionName='Add Goal or Event'
      onAction={ handleAddLayer }
      onCancel={ onCancel }
      actionTheme={ addLayerIsLocked ? 'golden' : 'blue' }
    />
  ), [addLayerIsLocked, onCancel, handleAddLayer])

  const subscriptionNote = addLayerIsLocked && (
    <div className={ styles.subscriptionNote }>
      Upgrade to Premium to add <br /> unlimited goals and events.
    </div>
  )

  const errorChild = useMemo(() => (
    <FuturePaneOverlay type='goal' />
  ), [])

  return (
    <FloatingPaneLayout
      type='future'
      header={ headerChild }
      footer={ footerChild }
      bodyClassName={ styles.futureMemberLayersPane }
      notification={ errorChild }
      withStaticHeader
      onClose={ onCancel }
    >
      <ul className={ styles.layersWrapper }>
        { layersChild }
      </ul>
      { subscriptionNote }
      { isLoading && (
        <LoadingOverlay />
      )}
    </FloatingPaneLayout>
  )

}
